const USER_HEADER = 'X-CT-User-Id';
const CLASSROOM_HEADER = 'X-CT-Classroom-Id';
const USER_QUERY_PARAM = 'ct_user_id';
const DEFAULT_BASE_PATH = typeof LIBRARIAN_BASE_PATH !== 'undefined' ? LIBRARIAN_BASE_PATH : '/librarian';

const buildHeaders = (userId, classroomId, extra) => {
  const headers = Object.assign(
    {
      'Content-Type': 'application/json',
    },
    extra || {},
  );
  if (userId) {
    headers[USER_HEADER] = userId;
  }
  if (classroomId) {
    headers[CLASSROOM_HEADER] = classroomId;
  }
  return headers;
};

const handleJsonResponse = async (response) => {
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    const message = detail || 'Librarian request failed';
    throw new Error(message);
  }
  return response.json();
};

const normaliseFilters = (filters = {}) => {
  const result = {};
  Object.keys(filters || {}).forEach((key) => {
    const value = filters[key];
    if (Array.isArray(value)) {
      const cleaned = value.map((entry) => (entry || '').toString().trim()).filter(Boolean);
      if (cleaned.length) {
        result[key] = cleaned;
      }
      return;
    }
    if (value !== undefined && value !== null && `${value}`.trim()) {
      result[key] = [`${value}`.trim()];
    }
  });
  return result;
};

const appendFiltersToParams = (params, filters = {}) => {
  const normalised = normaliseFilters(filters);
  Object.keys(normalised).forEach((key) => {
    normalised[key].forEach((value) => {
      params.append(`filters[${key}]`, value);
    });
  });
};

const appendEnableQA = (params, enableQA) => {
  if (typeof enableQA === 'boolean') {
    params.append('enable_qa', enableQA ? 'true' : 'false');
  }
};

const appendEnabledVideo = (params, enabledVideo) => {
  if (typeof enabledVideo === 'boolean') {
    params.append('enabled_video', enabledVideo ? 'true' : 'false');
  }
};

const buildStreamUrl = ({ message, filters, userId, classroomId, enableQA, enabledVideo }) => {
  const params = new URLSearchParams();
  params.append('message', message);
  params.append('stream_mode', 'semantic');
  if (userId) {
    params.append(USER_QUERY_PARAM, userId);
  }
  if (classroomId) {
    params.append('ct_classroom_id', classroomId);
  }
  appendFiltersToParams(params, filters);
  appendEnableQA(params, enableQA);
  appendEnabledVideo(params, enabledVideo);
  return `${DEFAULT_BASE_PATH}/api/chat-stream?${params.toString()}`;
};

const buildChatUrl = ({ enableQA, enabledVideo }) => {
  const params = new URLSearchParams();
  params.append('full', 'true');
  appendEnableQA(params, enableQA);
  appendEnabledVideo(params, enabledVideo);
  return `${DEFAULT_BASE_PATH}/api/chat?${params.toString()}`;
};

const fetchSession = ({ userId, classroomId } = {}) =>
  fetch(`${DEFAULT_BASE_PATH}/api/session`, {
    credentials: 'include',
    headers: buildHeaders(userId, classroomId),
  }).then(handleJsonResponse);

const sendChatMessage = ({ message, filters = {}, userId, classroomId, enableQA, enabledVideo }) =>
  fetch(buildChatUrl({ enableQA, enabledVideo }), {
    method: 'POST',
    credentials: 'include',
    headers: buildHeaders(userId, classroomId),
    body: JSON.stringify({ message, filters: normaliseFilters(filters) }),
  }).then(handleJsonResponse);

const resetSession = ({ userId, classroomId } = {}) =>
  fetch(`${DEFAULT_BASE_PATH}/api/reset`, {
    method: 'POST',
    credentials: 'include',
    headers: buildHeaders(userId, classroomId),
  }).then(handleJsonResponse);

const openChatStream = ({ message, filters = {}, userId, classroomId, enableQA, enabledVideo }) => {
  if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
    throw new Error('EventSource is not available in this environment.');
  }
  const url = buildStreamUrl({ message, filters, userId, classroomId, enableQA, enabledVideo });
  return new window.EventSource(url);
};

module.exports = {
  fetchSession,
  sendChatMessage,
  resetSession,
  openChatStream,
};
